/**
 * What a reminder takes you to. A reminder's `link` (stored reminders) or a task's `opens` (a task's own reminder) is
 * { type, id }; the ring shows "Open note", "Start routine"… and the app routes by type. Pure.
 *
 *   task · project · note · event · process (routine)   → an id in the store
 *   page                                                 → an in-app screen (PAGES key: 'time', 'log', …)
 *   url                                                  → a web address (opens in the browser)
 *   app                                                  → a deep link into another app (APP_LINKS key) — the
 *                                                          registry is empty until the BattleSuite apps publish
 *                                                          their links (e.g. BattleBooks → run payroll)
 */
export const TARGET_TYPES = [
  { type: 'task', label: 'Task', icon: 'check-square', open: 'Open task' },
  { type: 'project', label: 'Job', icon: 'folder', open: 'Open job' },
  { type: 'note', label: 'Note', icon: 'file-text', open: 'Open note' },
  { type: 'event', label: 'Event', icon: 'calendar', open: 'Open event' },
  { type: 'process', label: 'Routine', icon: 'repeat', open: 'Start routine' },
  { type: 'page', label: 'Screen', icon: 'layout', open: 'Go there' },
  { type: 'url', label: 'Link', icon: 'external-link', open: 'Open link' },
];

/** In-app screens a reminder can send you to. `web` = the desktop route; the phone maps the key to its screen. */
export const PAGES = [
  { id: 'plan', label: 'Plan the day', web: '/plan' },
  { id: 'week', label: 'Week', web: '/week' },
  { id: 'calendar', label: 'Calendar', web: '/calendar' },
  { id: 'time', label: 'Time sheets', web: '/time' },
  { id: 'log', label: 'Daily log', web: '/log' },
  { id: 'insights', label: 'Insights', web: '/insights' },
  { id: 'trips', label: 'Trips', web: '/trips' },
  { id: 'notes', label: 'Notes', web: '/notes' },
  { id: 'approvals', label: 'Approvals', web: '/approvals' },
  { id: 'team', label: 'Team', web: '/team' },
];

/**
 * Other apps' deep links: { id, app, label, web?, native? } — e.g. { id: 'battlebooks.payroll', app: 'BattleBooks',
 * label: 'Run payroll', … }. Empty until those links exist; `app` targets are offered only when it has entries.
 */
export const APP_LINKS = [];

const byType = new Map(TARGET_TYPES.map((t) => [t.type, t]));
export const targetType = (type) => byType.get(type) || null;

/** A clean { type, id } or null (unknown type, empty id, a url without a scheme gets https://). */
export function normalizeTarget(link) {
  if (!link || !link.type || link.id == null || String(link.id).trim() === '') return null;
  const type = String(link.type);
  let id = String(link.id).trim();
  if (type === 'url') id = /^[a-z][a-z0-9+.-]*:/i.test(id) ? id : `https://${id}`;
  if (type === 'page' && !PAGES.some((p) => p.id === id)) return null;
  if (type === 'app' && !APP_LINKS.some((a) => a.id === id)) return null;
  if (!byType.has(type) && !['approval', 'block', 'routine', 'app'].includes(type)) return null;
  return { type, id };
}

/** The button on the ring for this target. */
export function targetOpenLabel(link) {
  if (!link) return null;
  if (link.type === 'approval') return 'Review';
  if (link.type === 'block') return 'Open plan';
  if (link.type === 'routine') return 'Start routine';
  if (link.type === 'app') return APP_LINKS.find((a) => a.id === link.id)?.label || 'Open app';
  if (link.type === 'page') return `Go to ${PAGES.find((p) => p.id === link.id)?.label || 'screen'}`;
  return targetType(link.type)?.open || 'Open';
}

/**
 * How to show a target: { icon, label, sub, missing } — looked up in what the app has loaded
 * (ctx: { tasks, projects, notes, events, processes }). `missing` = it points at something that's gone.
 */
export function describeTarget(link, ctx = {}) {
  if (!link) return null;
  const find = (list) => (list || []).find((x) => x.id === link.id) || null;
  const t = targetType(link.type);
  const icon = t?.icon || (link.type === 'approval' ? 'shield' : link.type === 'app' ? 'smartphone' : 'link');
  switch (link.type) {
    case 'task': { const x = find(ctx.tasks); const p = x?.projectId ? (ctx.projects || []).find((q) => q.id === x.projectId) : null; return { icon, label: x?.title || 'Task', sub: p?.name || null, missing: !x }; }
    case 'project': { const x = find(ctx.projects); return { icon, label: x ? `${x.icon ? `${x.icon} ` : ''}${x.name}` : 'Job', sub: null, missing: !x }; }
    case 'note': { const x = find(ctx.notes); return { icon, label: x?.title || 'Note', sub: null, missing: !x || !!x.deletedAt }; }
    case 'event': { const x = find(ctx.events); return { icon, label: x?.title || 'Event', sub: null, missing: !x }; }
    case 'process': case 'routine': { const x = find(ctx.processes); return { icon: 'repeat', label: x?.name || x?.title || 'Routine', sub: null, missing: !x }; }
    case 'page': { const p = PAGES.find((q) => q.id === link.id); return { icon, label: p?.label || link.id, sub: 'BattleOps', missing: !p }; }
    case 'url': { let host = link.id; try { host = new URL(link.id).host; } catch { /* keep as typed */ } return { icon, label: host, sub: link.id, missing: false }; }
    case 'app': { const a = APP_LINKS.find((q) => q.id === link.id); return { icon, label: a?.label || link.id, sub: a?.app || null, missing: !a }; }
    default: return { icon, label: link.type, sub: null, missing: false };
  }
}
